import { AxiosError } from 'axios';

import type { AuthErrorPayload } from '@/api/auth.types';

/**
 * Normalise une erreur axios/JS en `AuthErrorPayload` pour l'UI.
 * Cible Phase 1 : login email/password.
 */
/** Extrait le champ `detail` d'une réponse axios, ou renvoie undefined. */
function extractDetail(error: AxiosError): string | undefined {
  const data = error.response?.data;
  if (data !== null && typeof data === 'object' && 'detail' in data) {
    const detail = (data as Record<string, unknown>)['detail'];
    if (typeof detail === 'string' && detail.length > 0) {
      return detail;
    }
  }
  return undefined;
}

export function mapAuthError(error: unknown): AuthErrorPayload {
  if (error instanceof AxiosError) {
    if (error.response !== undefined) {
      const status = error.response.status;
      if (status === 401) {
        return {
          kind: 'invalid_credentials',
          message: 'Identifiants invalides. Vérifiez votre e-mail et votre mot de passe.',
        };
      }
      if (status === 403) {
        const detail = extractDetail(error);
        return {
          kind: 'forbidden_audience',
          message:
            detail ?? 'Ce compte n’est pas autorisé pour cette application.',
        };
      }
      if (status === 423) {
        return {
          kind: 'account_locked',
          message:
            'Compte temporairement verrouillé après trop de tentatives. Réessayez plus tard.',
        };
      }
      if (status === 429) {
        const retryHeader = error.response.headers['retry-after'];
        const retryAfter =
          typeof retryHeader === 'string' && retryHeader.length > 0
            ? Number(retryHeader)
            : undefined;
        return {
          kind: 'rate_limited',
          message: 'Trop de tentatives. Patientez quelques minutes avant de réessayer.',
          ...(retryAfter !== undefined && Number.isFinite(retryAfter) ? { retryAfter } : {}),
        };
      }
      if (status >= 500) {
        return {
          kind: 'server',
          message: 'Le serveur est temporairement indisponible. Réessayez dans un instant.',
        };
      }
      return {
        kind: 'unknown',
        message: 'Une erreur inattendue est survenue. Réessayez.',
      };
    }
    return {
      kind: 'network',
      message: 'Connexion impossible. Vérifiez votre réseau.',
    };
  }
  return {
    kind: 'unknown',
    message: 'Une erreur inattendue est survenue. Réessayez.',
  };
}
